import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Clock, AlertTriangle, CheckCircle2, Send,
  Filter, X, Loader2, User, Tag, ChevronRight, BarChart3, Inbox
} from 'lucide-react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const sadApi = () => {
  const token = localStorage.getItem('sad_token');
  return {
    get: (url) => fetch(`${API_BASE}/api${url}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    post: (url, body) => fetch(`${API_BASE}/api${url}`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json()),
    put: (url, body) => fetch(`${API_BASE}/api${url}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json()),
  };
};

const STATUS_STYLES = {
  open: { bg: 'bg-green-500/10 text-green-400 border-green-500/20', dot: 'bg-green-500' },
  in_progress: { bg: 'bg-blue-500/10 text-blue-400 border-blue-500/20', dot: 'bg-blue-500' },
  waiting: { bg: 'bg-amber-500/10 text-amber-400 border-amber-500/20', dot: 'bg-amber-500' },
  resolved: { bg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'bg-emerald-500' },
  closed: { bg: 'bg-slate-500/10 text-slate-400 border-slate-500/20', dot: 'bg-slate-500' },
};

const PRIO_STYLES = {
  low: 'text-slate-400',
  medium: 'text-blue-400',
  high: 'text-orange-400',
  urgent: 'text-red-400',
};

const SuperAdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterCategory) params.set('category', filterCategory);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const [ticketsData, statsData] = await Promise.all([
        sadApi().get(`/sad/tickets${qs}`),
        sadApi().get('/sad/tickets/stats'),
      ]);
      setTickets(Array.isArray(ticketsData) ? ticketsData : []);
      setStats(statsData || {});
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
    setLoading(false);
  }, [filterStatus, filterCategory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      const updated = await sadApi().post(`/sad/tickets/${selected.id}/reply`, { message: replyText });
      setSelected(updated);
      setReplyText('');
      fetchData();
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const updateTicket = async (ticketId, updates) => {
    try {
      const updated = await sadApi().put(`/sad/tickets/${ticketId}`, updates);
      setSelected(updated);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const isSLABreached = (ticket) => {
    if (['resolved', 'closed'].includes(ticket.status)) return false;
    return ticket.sla_deadline && new Date(ticket.sla_deadline) < new Date();
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div data-testid="sad-tickets-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-amber-400" />
            Support Tickets
          </h1>
          <p className="text-slate-400 text-sm mt-1">Manage customer support tickets</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {[
            { label: 'Total', val: stats.total || 0, icon: BarChart3, color: 'text-white' },
            { label: 'Open', val: stats.open || 0, icon: Inbox, color: 'text-green-400' },
            { label: 'In Progress', val: stats.in_progress || 0, icon: Clock, color: 'text-blue-400' },
            { label: 'Waiting', val: stats.waiting || 0, icon: Clock, color: 'text-amber-400' },
            { label: 'Resolved', val: stats.resolved || 0, icon: CheckCircle2, color: 'text-emerald-400' },
            { label: 'Closed', val: stats.closed || 0, icon: CheckCircle2, color: 'text-slate-400' },
            { label: 'SLA Breach', val: stats.sla_breached || 0, icon: AlertTriangle, color: 'text-red-400' },
          ].map(s => (
            <div key={s.label} className="bg-slate-800/50 border border-slate-700 rounded-sm p-3" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
              <s.icon className={`w-4 h-4 ${s.color} mb-1`} />
              <div className={`text-xl font-bold ${s.color}`}>{s.val}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-4 h-4 text-slate-400" />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-sm px-3 py-1.5" data-testid="filter-status">
            <option value="">All Status</option>
            {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-sm px-3 py-1.5" data-testid="filter-category">
            <option value="">All Categories</option>
            {['billing', 'technical', 'account', 'general'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(filterStatus || filterCategory) && (
            <button onClick={() => { setFilterStatus(''); setFilterCategory(''); }}
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        {/* Tickets List */}
        <div className="space-y-2">
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400">No tickets found</p>
            </div>
          ) : tickets.map(ticket => {
            const sty = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;
            const breached = isSLABreached(ticket);
            return (
              <div key={ticket.id}
                onClick={() => setSelected(ticket)}
                className={`bg-slate-800/40 border ${breached ? 'border-red-500/40' : 'border-slate-700'} rounded-sm p-4 cursor-pointer hover:bg-slate-800/70 transition-all`}
                data-testid={`sad-ticket-${ticket.ticket_number}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-slate-500">{ticket.ticket_number}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sty.bg}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                      {breached && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> SLA
                        </span>
                      )}
                    </div>
                    <h3 className="font-medium text-white text-sm truncate">{ticket.subject}</h3>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <User className="w-3 h-3" />{ticket.user_email || ticket.user_name}
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Tag className="w-3 h-3" />{ticket.category}
                      </span>
                      <span className={`text-xs ${PRIO_STYLES[ticket.priority]}`}>
                        {ticket.priority}
                      </span>
                      <span className="text-xs text-slate-500">
                        <MessageSquare className="w-3 h-3 inline mr-1" />{ticket.messages?.length || 0}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-slate-500">{new Date(ticket.created_at).toLocaleDateString()}</p>
                    <ChevronRight className="w-4 h-4 text-slate-600 mt-1 ml-auto" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ticket Detail Slide-over */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/60 z-50" onClick={() => setSelected(null)} />
          <div className="fixed right-0 top-0 bottom-0 w-full md:w-[650px] bg-[#0f172a] z-50 flex flex-col border-l border-slate-700 shadow-2xl"
            data-testid="sad-ticket-detail">
            {/* Header */}
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-mono text-slate-500">{selected.ticket_number}</span>
                <button onClick={() => setSelected(null)} className="p-1 hover:bg-slate-800 rounded-sm">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <h3 className="text-lg font-bold text-white">{selected.subject}</h3>
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                <User className="w-3 h-3" />{selected.user_email}
                <span className="mx-1">|</span>
                <Clock className="w-3 h-3" />{new Date(selected.created_at).toLocaleString()}
                {isSLABreached(selected) && (
                  <span className="text-red-400 flex items-center gap-1 ml-2">
                    <AlertTriangle className="w-3 h-3" /> SLA Breached
                  </span>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex items-center gap-2 mt-3">
                <select value={selected.status}
                  onChange={e => updateTicket(selected.id, { status: e.target.value })}
                  className="bg-slate-800 border border-slate-600 text-slate-300 text-xs rounded-sm px-2 py-1"
                  data-testid="ticket-status-select">
                  {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
                <select value={selected.priority}
                  onChange={e => updateTicket(selected.id, { priority: e.target.value })}
                  className="bg-slate-800 border border-slate-600 text-slate-300 text-xs rounded-sm px-2 py-1"
                  data-testid="ticket-priority-select">
                  {['low', 'medium', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={selected.category}
                  onChange={e => updateTicket(selected.id, { category: e.target.value })}
                  className="bg-slate-800 border border-slate-600 text-slate-300 text-xs rounded-sm px-2 py-1"
                  data-testid="ticket-category-select">
                  {['billing', 'technical', 'account', 'general'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {selected.messages?.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-sm ${
                    msg.sender === 'admin'
                      ? 'bg-amber-500/10 border border-amber-500/20'
                      : 'bg-slate-800 border border-slate-700'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${msg.sender === 'admin' ? 'text-amber-400' : 'text-slate-400'}`}>
                        {msg.sender_name}
                      </span>
                      <span className="text-[10px] text-slate-500">{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-slate-200 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply */}
            <div className="p-4 border-t border-slate-700">
              <div className="flex gap-2">
                <input type="text" value={replyText} onChange={e => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  className="flex-1 bg-slate-800 border border-slate-600 text-white px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-amber-500"
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()}
                  data-testid="sad-ticket-reply-input" />
                <button onClick={handleReply} disabled={sending || !replyText.trim()}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-sm flex items-center gap-1 text-sm disabled:opacity-50"
                  data-testid="sad-ticket-reply-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminTickets;
